import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Link from "@mui/material/Link";
import ConfigData from "../config/config.json";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

const collator = new Intl.Collator("de", { sensitivity: "base" });
const webserver = ConfigData.WEBSERVER;

function translateExaminationEnum(enumElement) {
  switch (enumElement) {
    case "NOT_EXAMINED":
      return "Ungeprüft";
    case "PASSED":
      return "Bestanden";
    case "FAILED":
      return "Durchgefallen";
    case "MULTIPLE_FAILURES":
      return "Wiederholt durchgefallen";
    case "PASSED_AFTER_FAILURE":
      return "Wiederholung bestanden";
    default:
      return "Ausstehend";
  }
}

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (orderBy === "id") {
    if (b.id < a.id) {
      return -1;
    }
    if (b.id > a.id) {
      return 1;
    }
    return 0;
  }

  if (orderBy === "name") {
    //return a[orderBy].localeCompare(b[orderBy], 'de', {sensitivity: 'base'})
    return collator.compare(a[orderBy], b[orderBy]);
  }

  if (orderBy === "result") {
    if(a.result.result === "NOT_EXAMINED" && b.result.result !== "NOT_EXAMINED") {
      return 1;
    }
    if(a.result.result !== "NOT_EXAMINED" && b.result.result === "NOT_EXAMINED") {
      return -1;
    }
    if(a.result.result === "NOT_EXAMINED" && b.result.result === "NOT_EXAMINED") {
      return descendingComparator(a, b, "id");
    } else {
      return collator.compare(a.result.result, b.result.result);
    }
  }

  if (orderBy === "examinationDate") {
    if (a.result.examinationDate === "-/-" && b.result.examinationDate !== "-/-") {
      return 1;
    } else if (a.result.examinationDate !== "-/-" && b.result.examinationDate === "-/-") {
      return -1;
    } else if (a.result.examinationDate === "-/-" && b.result.examinationDate === "-/-") {
      return descendingComparator(a, b, "id");
    } else {
      if (b.result.examinationDate.substring(6) < a.result.examinationDate.substring(6)) {
        return -1;
      } else if (b.result.examinationDate.substring(6) > a.result.examinationDate.substring(6)) {
        return 1;
      } else {
        if (b.result.examinationDate.substring(3, 6) < a.result.examinationDate.substring(3, 6)) {
          return -1;
        } else if (b.result.examinationDate.substring(3, 6) > a.result.examinationDate.substring(3, 6)) {
          return 1;
        } else {
          if (b.result.examinationDate.substring(0, 3) < a.result.examinationDate.substring(0, 3)) {
            return -1;
          } else if (b.result.examinationDate.substring(0, 3) > a.result.examinationDate.substring(0, 3)) {
            return 1;
          } else {
            return 0;
          }
        }
      }
    }
  }
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "Apo. Nr.",
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Apotheke",
  },
  {
    id: "examinationDate",
    numeric: false,
    disablePadding: false,
    label: "Prüfdatum",
  },
  {
    id: "result",
    numeric: false,
    disablePadding: false,
    label: "Ergebnis",
  },
  {
    id: "document",
    numeric: false,
    disablePadding: false,
    label: "Original",
  },
  {
    id: "documentCorrection",
    numeric: false,
    disablePadding: false,
    label: "Korrektur",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function BLAKProcessPharmaciesListArchive(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [customPharmacyErrorMessage] = useState(null);

  const [rowss, setRowss] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/examProcess/getBLAK/" +
        props.processID,
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        setRowss(data);
      })
      .catch((error) => {
        //TODO global error cannot load user table
      });
  }, [props.processID, customPharmacyErrorMessage]);

  const filteredRows = rowss.filter((row) => {
    return (
      row.id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.name.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowss.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={"medium"}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRowss={setRowss}
              rowss={rowss}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rowss.length; found++) {
                    if (rowss[found].result.id === row.result.id) {
                      index = found;
                      break;
                    }
                  }
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.result.id}>
                      <TableCell id={labelId} scope="row">
                        {row.id}
                      </TableCell>
                      {/* <TableCell style={{whiteSpace: "normal", wordBreak: "break-word", width:300}} align="left"> {row.name.length > 33 ? row.name.substring(0, 30) + " ..." : row.name}</TableCell> */}
                      {row.name.length > 25 ? (
                        <TableCell align="left">
                          <Tooltip title={row.name} enterDelay={1000}>
                            <Typography
                              sx={{
                                minWidth: 200,
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        )
                        :
                        (
                          <TableCell align="left">
                            <Typography
                              sx={{
                                minWidth: 200,
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.name}
                            </Typography>
                          </TableCell>
                        )
                      }
                      <TableCell align="left">
                        {row.result.examinationDate}
                      </TableCell>
                      <TableCell align="left">
                        {translateExaminationEnum(row.result.result)}
                      </TableCell>
                      <TableCell align="center">
                        {row.result.filePath !== null ? (
                          <Link
                            href={
                              webserver.protocol +
                              "://" +
                              webserver.host +
                              ":" +
                              webserver.port +
                              "/examResult/files/" +
                              row.result.id
                            }
                          >
                              <Tooltip title={"Dokumentationsbogen herunterladen"} enterDelay={1000}>
                                <IconButton>
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        {row.result.filePathCorrection !== null ? (
                          <Link
                            href={
                              webserver.protocol +
                              "://" +
                              webserver.host +
                              ":" +
                              webserver.port +
                              "/examResult/correctionFiles/" +
                              row.result.id
                            }
                          >
                              <Tooltip title={"Korrektur herunterladen"} enterDelay={1000}>
                                <IconButton>
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowss.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
